/**
 * AdminAffiliates.tsx
 *
 * Admin interface для управления affiliate программой
 *
 * Функции:
 * - Просмотр и одобрение pending affiliate applications
 * - Просмотр и одобрение pending commissions
 * - Отметка commissions как paid
 * - Общая статистика affiliate программы
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../api';
import './AdminAffiliates.css';

interface AffiliateApplication {
  id: number;
  user_id: number;
  affiliate_code: string;
  payment_email: string;
  created_at: string;
  user_email: string;
}

interface PendingCommission {
  id: number;
  affiliate_id: number;
  amount: number;
  commission_type: string;
  transaction_amount: number | null;
  created_at: string;
  affiliate_code: string;
  payment_email: string;
}

interface AffiliateStats {
  affiliates: {
    total: number;
    active: number;
    pending: number;
    total_earnings: number;
    pending_payout: number;
  };
  commissions: Record<string, { count: number; total: number }>;
  referrals: {
    total: number;
    converted: number;
    conversion_rate: number;
  };
}

const AdminAffiliates: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'applications' | 'commissions' | 'stats'>('applications');
  const [applications, setApplications] = useState<AffiliateApplication[]>([]);
  const [commissions, setCommissions] = useState<PendingCommission[]>([]);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiRequest('/affiliates/admin/pending-applications', {
        method: 'GET'
      });

      setApplications(data.applications);
    } catch (err) {
      setError('Ошибка загрузки заявок');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCommissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiRequest('/affiliates/admin/pending-commissions', {
        method: 'GET'
      });

      setCommissions(data.commissions);
    } catch (err) {
      setError('Ошибка загрузки комиссий');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiRequest('/affiliates/admin/stats', {
        method: 'GET'
      });

      setStats(data);
    } catch (err) {
      setError('Ошибка загрузки статистики');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const approveAffiliate = async (id: number) => {
    try {
      await apiRequest('/affiliates/admin/approve-affiliate', {
        method: 'POST',
        body: JSON.stringify({ id })
      });

      alert('✅ Affiliate одобрен!');
      loadApplications(); // Reload list
    } catch (err) {
      alert('❌ Ошибка одобрения affiliate');
      console.error(err);
    }
  };

  const approveCommission = async (id: number) => {
    try {
      await apiRequest('/affiliates/admin/approve-commission', {
        method: 'POST',
        body: JSON.stringify({ id })
      });

      alert('✅ Комиссия одобрена!');
      loadCommissions(); // Reload list
    } catch (err) {
      alert('❌ Ошибка одобрения комиссии');
      console.error(err);
    }
  };

  const markCommissionPaid = async (commissionId: number, paymentReference: string) => {
    try {
      await apiRequest('/affiliates/admin/mark-commission-paid', {
        method: 'POST',
        body: JSON.stringify({ commission_id: commissionId, payment_reference: paymentReference })
      });

      alert('✅ Комиссия отмечена как выплаченная!');
      loadCommissions(); // Reload list
    } catch (err) {
      alert('❌ Ошибка отметки выплаты');
      console.error(err);
    }
  };

  useEffect(() => {
    if (activeTab === 'applications') {
      loadApplications();
    } else if (activeTab === 'commissions') {
      loadCommissions();
    } else if (activeTab === 'stats') {
      loadStats();
    }
  }, [activeTab]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMoney = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };

  if (!user) {
    return (
      <div className="admin-affiliates-container">
        <div className="auth-required">
          <h2>Требуется авторизация</h2>
          <p>Войдите как администратор для доступа к этой странице</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-affiliates-container">
      {/* Header */}
      <div className="admin-header">
        <h1>👨‍💼 Admin: Affiliate Management</h1>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
          onClick={() => setActiveTab('applications')}
        >
          📝 Заявки ({applications.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'commissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('commissions')}
        >
          💸 Комиссии ({commissions.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          📊 Статистика
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="loading">
          <div className="spinner-border text-primary" role="status"></div>
          <p>Загрузка...</p>
        </div>
      )}

      {/* Applications Tab */}
      {!loading && activeTab === 'applications' && (
        <div className="admin-section">
          <h2>Pending Affiliate Applications</h2>
          {applications.length === 0 ? (
            <div className="empty-state">
              <p>Нет pending заявок</p>
            </div>
          ) : (
            <div className="applications-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User Email</th>
                    <th>Affiliate Code</th>
                    <th>Payment Email</th>
                    <th>Created</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app.id}>
                      <td>{app.id}</td>
                      <td>{app.user_email}</td>
                      <td><code>{app.affiliate_code}</code></td>
                      <td>{app.payment_email}</td>
                      <td>{formatDate(app.created_at)}</td>
                      <td>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => approveAffiliate(app.id)}
                        >
                          ✅ Одобрить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Commissions Tab */}
      {!loading && activeTab === 'commissions' && (
        <div className="admin-section">
          <h2>Pending Commissions</h2>
          {commissions.length === 0 ? (
            <div className="empty-state">
              <p>Нет pending комиссий</p>
            </div>
          ) : (
            <div className="commissions-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Affiliate Code</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Transaction</th>
                    <th>Payment Email</th>
                    <th>Created</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((comm) => (
                    <tr key={comm.id}>
                      <td>{comm.id}</td>
                      <td><code>{comm.affiliate_code}</code></td>
                      <td>{comm.commission_type}</td>
                      <td><strong>{formatMoney(comm.amount)}</strong></td>
                      <td>{comm.transaction_amount ? formatMoney(comm.transaction_amount) : 'N/A'}</td>
                      <td>{comm.payment_email}</td>
                      <td>{formatDate(comm.created_at)}</td>
                      <td>
                        <button
                          className="btn btn-success btn-sm me-1"
                          onClick={() => approveCommission(comm.id)}
                        >
                          ✅ Одобрить
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            const ref = prompt('Введите PayPal transaction ID:');
                            if (ref) {
                              markCommissionPaid(comm.id, ref);
                            }
                          }}
                        >
                          💳 Отметить выплаченной
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {!loading && activeTab === 'stats' && stats && (
        <div className="admin-section">
          <h2>Статистика Affiliate Programme</h2>

          {/* Affiliate Stats */}
          <div className="stats-group">
            <h3>Affiliates</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total</div>
                <div className="stat-value">{stats.affiliates.total}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Active</div>
                <div className="stat-value">{stats.affiliates.active}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Pending</div>
                <div className="stat-value">{stats.affiliates.pending}</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Total Earnings</div>
                <div className="stat-value">{formatMoney(stats.affiliates.total_earnings)}</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Pending Payout</div>
                <div className="stat-value">{formatMoney(stats.affiliates.pending_payout)}</div>
              </div>
            </div>
          </div>

          {/* Commissions Stats */}
          <div className="stats-group">
            <h3>Commissions</h3>
            <div className="stats-grid">
              {Object.entries(stats.commissions).map(([status, data]) => (
                <div key={status} className="stat-card">
                  <div className="stat-label">{status}</div>
                  <div className="stat-value">{data.count}</div>
                  <div className="stat-sublabel">{formatMoney(data.total)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Referrals Stats */}
          <div className="stats-group">
            <h3>Referrals & Conversions</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Referrals</div>
                <div className="stat-value">{stats.referrals.total}</div>
                <div className="stat-sublabel">Registrations</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Converted</div>
                <div className="stat-value">{stats.referrals.converted}</div>
                <div className="stat-sublabel">Paid customers</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Conversion Rate</div>
                <div className="stat-value">{stats.referrals.conversion_rate}%</div>
                <div className="stat-sublabel">CR%</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliates;
